import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import type {
  ModelsClubProfileResponse,
  ModelsFeaturedClub,
  ModelsUserFullInfoResponse
} from '@/api/generated-api'
import type {
  ActivationSummary,
  ClubOwnerProfile,
  ClubPublicProfile,
  FansPage,
  ProgressStep
} from '@/features/clubs/types'
import {
  currentAccountType,
  newRequestId,
  track,
  withRequestId
} from '@/lib/productAnalytics'
import { createInfiniteHook, createQueryHook } from '@/shared/api'
import api, { apiRequest } from '@/shared/api/client'
import { isAuthenticated } from '@/utils/auth'

import { ATHLETE_VIEWER_FLAGS_KEY } from '../lib/athleteProfile'
import {
  CLUBS_LIST_PAGE_LIMIT,
  fetchClubsListPage,
  type ParticipantFilter
} from '../lib/clubsListApi'
import {
  FAVORITE_CHANGE_KEY,
  type FavoriteChangeAvailability
} from '../lib/favoriteChange'

export { useClubsSearch } from './useClubsSearch'

export const enum ClubsQueryKeys {
  CLUB_DETAIL = 'club_detail',
  CLUB_PUBLIC_PROFILE = 'club_public_profile',
  CLUB_OWNER_PROFILE = 'club_owner_profile',
  CLUB_FANS = 'club_fans',
  CLUBS_LIST = 'clubs_list',
  FEATURED_CLUBS = 'clubs_featured'
}

const FEATURED_CLUBS_LIMIT = 12

const getFeaturedClubs = async (): Promise<ModelsFeaturedClub[]> => {
  const response = await api.v2ClubsFeaturedList({
    limit: FEATURED_CLUBS_LIMIT
  })
  return response.clubs ?? []
}

const useFeaturedClubsFactory = createQueryHook<
  { enabled?: boolean },
  ModelsFeaturedClub[]
>({
  getQueryKey: () => [ClubsQueryKeys.FEATURED_CLUBS],
  queryFn: () => getFeaturedClubs(),
  getEnabled: params => params.enabled !== false,
  getOptions: () => ({
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true
  })
})

/** Public home-page featured clubs strip (`GET /api/v2/clubs/featured`). */
export function useFeaturedClubs(options?: { enabled?: boolean }) {
  return useFeaturedClubsFactory({ enabled: options?.enabled })
}

const getClubById = async ({ clubId }: { clubId: string }) => {
  const response = await api.v2ClubsDetail(clubId)
  return response.club || null
}

const useClubByIdFactory = createQueryHook<
  { clubId?: string },
  Awaited<ReturnType<typeof getClubById>>
>({
  getQueryKey: ({ clubId }) => [ClubsQueryKeys.CLUB_DETAIL, clubId],
  queryFn: ({ clubId }) => getClubById({ clubId: clubId || '' }),
  getEnabled: ({ clubId }) => Boolean(clubId),
  getOptions: () => ({ refetchOnMount: true })
})

export function useClubById(clubId?: string) {
  return useClubByIdFactory({ clubId })
}

// fan_views/global_rank are in the backend response but not yet in the live
// swagger that generated-api.ts is built from — narrow the response shape
// locally until the next codegen pass.
type ProfileResponseWithLeaderboardFields = ModelsClubProfileResponse & {
  fan_views?: number
  global_rank?: number
  /** BAT-1099: an athlete's place among athletes (instead of club ranks). */
  athlete_rank?: number
  /** BAT-1205: everything ever credited (balance_pts is net of withdrawals). */
  total_earned_pts?: number
}

// BAT-1220: a club not on the platform yet (awaiting approval) or hidden is
// a 404 for everyone but its owner. The owner opens its page through the
// signed-in read; for anyone else that read is the same 404.
const fetchClubProfile = async (clubId: string) => {
  try {
    return await api.v2ClubsProfileDetail(clubId)
  } catch (error) {
    const status = (error as { status?: number } | null)?.status
    if (status !== 404 || !isAuthenticated()) {
      throw error
    }
    return api.v2ClubsProfileMeDetail(clubId)
  }
}

const getClubPublicProfile = async ({
  clubId
}: {
  clubId: string
}): Promise<ClubPublicProfile | null> => {
  const response = (await fetchClubProfile(
    clubId
  )) as ProfileResponseWithLeaderboardFields
  if (!response?.club) return null
  return {
    club: response.club,
    is_owner: response.is_owner ?? false,
    club_rank: response.club_rank,
    global_rank: response.global_rank,
    balance_pts: response.balance_pts,
    total_earned_pts: response.total_earned_pts,
    fan_views: response.fan_views,
    challenges_completed: response.challenges_completed,
    athlete_rank: response.athlete_rank
  }
}

const useClubPublicProfileFactory = createQueryHook<
  { clubId?: string },
  ClubPublicProfile | null
>({
  getQueryKey: ({ clubId }) => [ClubsQueryKeys.CLUB_PUBLIC_PROFILE, clubId],
  queryFn: ({ clubId }) => getClubPublicProfile({ clubId: clubId || '' }),
  getEnabled: ({ clubId }) => Boolean(clubId),
  getOptions: () => ({
    // fan_count can change out-of-band (new fan registrations), so keep this
    // query responsive on tab/window focus.
    staleTime: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  })
})

/**
 * Публичный клубный профиль с leaderboard-обогащением (rank/ap_score/views/
 * challenges). Возвращает `ClubPublicProfile | null` — обёртка `{ club, ... }`
 * заменила прежний raw ModelsClub после wiring'а OCE-1159.
 */
export function useClubPublicProfile(clubId?: string) {
  return useClubPublicProfileFactory({ clubId })
}

/**
 * Owner-enriched данные клуба. Возвращает `activation_progress`.
 * OCE-653: подключён реальный GET /v2/clubs/profile/me (ADR-007). Ранее
 * хук возвращал пустой объект — ActivationProgressPanel не рендерился.
 */
export function useClubOwnerProfile({ enabled }: { enabled: boolean }) {
  return useQuery<ClubOwnerProfile>({
    queryKey: [ClubsQueryKeys.CLUB_OWNER_PROFILE],
    queryFn: async () => {
      const response = await api.v2ClubsProfileMeList()
      // Narrow loose Models* types to the strict ProgressStep shape the UI
      // expects. Drop any step missing name/status (shouldn't happen, but
      // keeps the downstream component free of undefined checks).
      const activation_progress: ProgressStep[] = (
        response.activation_progress ?? []
      ).flatMap(step => {
        if (!step.name || !step.status) return []
        const status = step.status as ProgressStep['status']
        return [{ name: step.name, status }]
      })
      // BAT-1101: not in the generated client yet; absent when clubs could
      // not answer (the panel then falls back to the club's fan count).
      const summary = (
        response as { activation_summary?: Partial<ActivationSummary> }
      ).activation_summary
      const activation_summary: ActivationSummary | undefined =
        summary && Number(summary.fans_threshold) > 0
          ? {
              fans_counted: Math.max(0, Number(summary.fans_counted) || 0),
              fans_threshold: Number(summary.fans_threshold),
              activated: summary.activated === true,
              monetization_available: summary.monetization_available === true
            }
          : undefined
      return { activation_progress, activation_summary }
    },
    enabled,
    staleTime: 60_000
  })
}

const useFansLeaderboardFactory = createInfiniteHook<
  {
    clubId?: string
    pageSize: number
    enabled: boolean
    region?: string
    sportType?: string
  },
  FansPage,
  number
>({
  getQueryKey: ({ clubId, pageSize, region, sportType }) => [
    ClubsQueryKeys.CLUB_FANS,
    clubId ?? 'global',
    sportType ?? 'all',
    pageSize,
    region ?? 'world'
  ],
  queryFn: async ({ params, pageParam }) => {
    const response = await apiRequest<FansPage>({
      path: `/api/v2/leaderboards/fans`,
      method: 'GET',
      query: {
        ...(params.clubId ? { club_id: params.clubId } : {}),
        ...(params.sportType ? { sport_type: params.sportType } : {}),
        ...(params.region && params.region !== 'world'
          ? { region: params.region }
          : {}),
        page: pageParam,
        page_size: params.pageSize
      },
      format: 'json'
    })

    return {
      items: response.items ?? [],
      previous_snapshot_available:
        response.previous_snapshot_available ?? false,
      page: response.page ?? pageParam,
      page_size: response.page_size ?? params.pageSize,
      total: response.total ?? 0,
      applied_region: response.applied_region
    }
  },
  initialPageParam: 0,
  getNextPageParam: lastPage => {
    const nextPage = lastPage.page + 1
    if (nextPage * lastPage.page_size < lastPage.total) return nextPage
  },
  getEnabled: ({ enabled }) => enabled,
  getOptions: () => ({ refetchOnMount: true })
})

const DEFAULT_CLUB_FANS_PAGE_SIZE = 20

export function useClubFans(
  clubId?: string,
  pageSize: number = DEFAULT_CLUB_FANS_PAGE_SIZE,
  region?: string
) {
  return useFansLeaderboardFactory({
    clubId,
    pageSize,
    enabled: Boolean(clubId),
    region
  })
}

export function useGlobalFans(
  pageSize: number = DEFAULT_CLUB_FANS_PAGE_SIZE,
  region?: string,
  sportType?: string
) {
  return useFansLeaderboardFactory({
    clubId: undefined,
    pageSize,
    enabled: true,
    region,
    sportType
  })
}

export function useClubsList(
  sportTypeId?: string,
  enabled = true,
  participantType?: ParticipantFilter
) {
  const query = useInfiniteQuery({
    queryKey: [ClubsQueryKeys.CLUBS_LIST, sportTypeId, participantType],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchClubsListPage({ sportTypeId, offset: pageParam, participantType }),
    getNextPageParam: (lastPage, pages) => {
      const pageLength = lastPage.clubs?.length ?? 0
      const loaded = pages.reduce(
        (count, page) => count + (page.clubs?.length ?? 0),
        0
      )
      if (
        pageLength === 0 ||
        (lastPage.total != null && loaded >= lastPage.total)
      ) {
        return undefined
      }
      return pageLength === CLUBS_LIST_PAGE_LIMIT || lastPage.total != null
        ? loaded
        : undefined
    },
    enabled,
    staleTime: 60_000,
    refetchOnMount: true
  })

  return {
    // eslint-disable-next-line @tanstack/query/no-rest-destructuring -- expose flattened list alongside pagination state
    ...query,
    data: query.data?.pages.flatMap(page => page.clubs ?? []) ?? []
  }
}

/** What «Become a fan» picks (BAT-1107): the new favourite and the one it
 * replaces, if any. */
export type BecomeFanInput = {
  clubId: string
  /** The new favourite is an athlete: changed_fields names the participant. */
  isAthlete?: boolean
  /** The current favourite (club or athlete) — its Fans drop by one. */
  previousClubId?: string
}

/**
 * Мутация «Стать фанатом» клуба или спортсмена — выставляет favorite_club_id
 * текущего юзера; при смене фаворита gateway проверяет правила (BAT-1100:
 * AG-304 — 30 дней, AG-305 — задания в работе).
 *
 * Использует PUT /v2/users/me (partial update: gateway мёрджит только non-nil
 * поля, остальной профиль не затрагивается; favorite_club_id XOR обнуляет
 * waitlist_club_id — инвариант OCE-634). Invalidate публичных профилей нового и
 * прежнего фаворита (fan_count ±1 обновится eventual через user.update
 * Kafka-событие) и профиля пользователя.
 */
export function useBecomeFan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ clubId, isAthlete }: BecomeFanInput) => {
      const rid = newRequestId()
      track({
        name: 'profile_update_submitted',
        request_id: rid,
        props: {
          account_type: currentAccountType(),
          // As the backend's profile_updated: sorted, the participant field
          // added when the favourite is an athlete (US-ATH-14).
          changed_fields: isAthlete
            ? 'favorite_club_id,favourite_participant_id'
            : 'favorite_club_id'
        }
      })
      await api.v2UsersMeUpdate(
        { favorite_club_id: clubId },
        withRequestId(rid)
      )
    },
    onSuccess: (_data, { clubId, previousClubId }) => {
      // Optimistically reflect the new favourite so the "Become a fan" button
      // hides immediately, before the /me refetch lands.
      queryClient.setQueriesData<ModelsUserFullInfoResponse>(
        { queryKey: ['me'] },
        prev =>
          prev?.user
            ? { ...prev, user: { ...prev.user, favorite_club_id: clubId } }
            : prev
      )
      for (const id of [clubId, previousClubId]) {
        if (!id) {
          continue
        }
        queryClient.invalidateQueries({
          queryKey: [ClubsQueryKeys.CLUB_PUBLIC_PROFILE, id]
        })
        // BAT-1106: the athlete page reads «You're a fan» from its own query.
        queryClient.invalidateQueries({
          queryKey: [ATHLETE_VIEWER_FLAGS_KEY, id]
        })
        queryClient.invalidateQueries({
          queryKey: [ClubsQueryKeys.CLUB_FANS, id]
        })
      }
      queryClient.invalidateQueries({ queryKey: [FAVORITE_CHANGE_KEY] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    }
  })
}

/** GET /api/v2/users/me/favorite-change — may the fan switch now (BAT-1100). */
export function fetchFavoriteChange(): Promise<FavoriteChangeAvailability> {
  return apiRequest<FavoriteChangeAvailability>({
    path: '/api/v2/users/me/favorite-change',
    method: 'GET',
    format: 'json'
  })
}

/**
 * Мутация «Перестать быть фанатом клуба» — очищает favorite_club_id юзера.
 *
 * Использует PUT /v2/users/me с favorite_club_id = "". api-gateway/user-profiles
 * после OCE-1291 трактует пустую строку как explicit clear (mergeUserUpdate
 * нулит поле → SQL NULL; user.update event несёт FavoriteClubID = &"" →
 * clubs consumer вызывает ClearFavoriteClub → удаление из club_fans +
 * decrement fan_count предыдущего клуба).
 *
 * @param prevClubId — clubId, с которого пользователь уходит; нужен для
 * invalidate CLUB_PUBLIC_PROFILE, чтобы fan_count перезагрузился после
 * Kafka-eventual обновления.
 */
export function useStopBeingFan(prevClubId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const rid = newRequestId()
      track({
        name: 'profile_update_submitted',
        request_id: rid,
        props: {
          account_type: currentAccountType(),
          changed_fields: 'favorite_club_id'
        }
      })
      await api.v2UsersMeUpdate({ favorite_club_id: '' }, withRequestId(rid))
    },
    onSuccess: () => {
      // Optimistically clear favourite locally so the Stop-button hides and the
      // page can flip back to Become-mode without waiting for the /me refetch.
      queryClient.setQueriesData<ModelsUserFullInfoResponse>(
        { queryKey: ['me'] },
        prev =>
          prev?.user
            ? { ...prev, user: { ...prev.user, favorite_club_id: undefined } }
            : prev
      )
      queryClient.invalidateQueries({
        queryKey: [ClubsQueryKeys.CLUB_PUBLIC_PROFILE, prevClubId]
      })
      // BAT-1180: the athlete page reads «fan» from its own query.
      queryClient.invalidateQueries({
        queryKey: [ATHLETE_VIEWER_FLAGS_KEY, prevClubId]
      })
      queryClient.invalidateQueries({
        queryKey: [ClubsQueryKeys.CLUB_FANS, prevClubId]
      })
      queryClient.invalidateQueries({ queryKey: [FAVORITE_CHANGE_KEY] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    }
  })
}
